import * as React from 'react';
import ToolNavMarkReview from '../tool/nav/ToolNavMarkReview';
import { IDocument, IDoc, ITokenized } from '../../types/my-types';
import { AppContext } from '../../App';
import { getIdFromUrl } from '../../utils/getIdFromUrl';

import { NotWorthIt } from '../../types/util';
import { ToolActionsContextProvider } from '../../context/ToolActionsContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGlynt from '../hooks/useGlynt';
import Loader from '../common/Loader';
import SpreadsheetMark from './SpreadsheetMark';
import ViewMarkValidationErrors from './ViewMarkValidationErrors';
import { useMarkValidation } from '../hooks/useMarkValidation';
import { useAtomValue } from 'jotai';
import { showMarkSpreadsheetAtom } from '../../atoms/mark-atoms';

interface MarkNavProps {
  steps: number;
  docIndex: number;
  documents: IDocument[];
  batchLabel: string;
  tokenizerVersion: string;
  trainingSetId: string;
  isFullscreen?: boolean;
  setIsFullscreen?: (v: boolean) => void;
}

const MarkNav: React.FC<MarkNavProps> = ({
  steps,
  docIndex,
  documents,
  batchLabel,
  tokenizerVersion,
  trainingSetId,
  isFullscreen,
  setIsFullscreen,
}) => {
  const { dataPoolId } = React.useContext(AppContext);
  const glynt = useGlynt();
  const client = useQueryClient();
  const showMarkSpreadsheet = useAtomValue(showMarkSpreadsheetAtom);
  const generatePage = useMutation({
    mutationFn: async (docId: string) =>
      (await glynt).document.createPages(docId)
  });

  const docs: IDoc[] = documents.map((doc, index) => ({
    id: doc.id,
    label: doc.label,
    index,
  }));
  const currentDoc = docs[docIndex % 100];

  // Get document IDs for validation:
  // - If grid is shown, SpreadsheetMark handles validation for all docs
  // - If grid is hidden, we validate only the current document here
  const documentIdsForValidation = React.useMemo(() => {
    if (showMarkSpreadsheet) {
      // SpreadsheetMark will handle validation when grid is visible
      return [];
    }
    return currentDoc?.id ? [currentDoc.id] : [];
  }, [showMarkSpreadsheet, currentDoc?.id]);

  // Fetch validation for current document when grid is hidden
  const { isLoading: isValidationLoading } = useMarkValidation({
    trainingSetId,
    documentIds: documentIdsForValidation,
    enabled: !showMarkSpreadsheet && !!currentDoc?.id,
  });

  if (!currentDoc) {
    return <Loader />;
  }

  const docLabel = currentDoc.label;
  const prefetchPage = () => {
    const doc = documents[(docIndex % 100) + 2];
    if (doc) {
      // TODO: it's kind of hacky to get the right version of tokenized, should api have the default successful tokenized for the doc?
      // will revisit this later
      // TODO: refactor to useTokenizedUrl
      client
        .fetchQuery({
          queryKey: ['tokenizeds', doc.id],
          queryFn: async () =>
            (await glynt).tokenizeds.list({
              document: doc.id,
              status: 'Success',
              ordering: '-created_at',
              advanced: true,
            }),
        })
        .then((data) => {
          let tokenizedUrl = '';
          if (data.results) {
            const token = data.results.filter(
              (tk: ITokenized) => tk.tokenizer_version === tokenizerVersion
            )[0];
            tokenizedUrl = token ? token.url : doc.tokenizeds[0];
          } else {
            tokenizedUrl = doc.tokenizeds[0];
          }

          const tokenizedId = getIdFromUrl(tokenizedUrl);
          client.prefetchQuery({
            queryKey: ['tokenized', tokenizedId],
            queryFn: async () => (await glynt).tokenizeds.retrieveRefreshed(tokenizedId),
          });
        })
        .catch((error: NotWorthIt) => {
          console.error(error);
        });

      generatePage.mutate(doc.id);
    }
  };

  return (
    <ToolActionsContextProvider>
      <ToolNavMarkReview
        batchLabel={batchLabel}
        steps={steps}
        docs={docs}
        docLabel={docLabel}
        docId={currentDoc.id}
        docIndex={docIndex}
        prefetchPage={prefetchPage}
        tokenizerVersion={tokenizerVersion}
        review={false}
        trainingSetId={trainingSetId}
        isFullscreen={isFullscreen}
        setIsFullscreen={setIsFullscreen}
        isMarkValidationLoading={isValidationLoading}
      />
      <SpreadsheetMark docs={documents} trainingSetId={trainingSetId} />
      <ViewMarkValidationErrors />
    </ToolActionsContextProvider>
  );
};
export default MarkNav;
